import {HttpEvent, HttpHandlerFn, HttpRequest} from '@angular/common/http';
import {inject} from '@angular/core';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {firstValueFrom, from, Observable, of} from 'rxjs';
import {catchError, filter, switchMap, take, timeout} from 'rxjs/operators';

const MAILBOX_API_PATTERNS = ['/imapApi-v1', '/gmailApi-v1'];

/**
 * Attaches the Firebase ID token to requests against the mailbox HTTP APIs
 * (imapApi-v1 / gmailApi-v1). Both functions reject unauthenticated calls.
 * All other requests pass through untouched.
 *
 * Uses authState (waits for the first non-null user) rather than a one-shot
 * currentUser snapshot, so a request fired right after an OAuth redirect — when
 * Firebase auth has not finished rehydrating yet — still carries the token
 * instead of racing to an empty header and getting a 401.
 */
export const mailboxAuthInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    if (!MAILBOX_API_PATTERNS.some(pattern => req.url.includes(pattern))) {
        return next(req);
    }

    const afAuth = inject(AngularFireAuth);

    const user$ = afAuth.authState.pipe(
        filter(user => !!user),
        take(1),
        timeout(5000),
        catchError(() => of(null))
    );

    return from(firstValueFrom(user$, {defaultValue: null})).pipe(
        switchMap(user => from(user?.getIdToken() ?? Promise.resolve(''))),
        switchMap(token => next(token
            ? req.clone({setHeaders: {Authorization: `Bearer ${token}`}})
            : req))
    );
};
